import {
    Component,
    inject,
    input,
    OnInit,
    signal,
    viewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { StorageService } from '@app/core/services/client-services/storage-service/storage.service';
import { ToastService } from '@app/core/services/toast.service';
import { RoutingEndpoints } from '@app/shared/models/routing/routing-endpoints';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { LoginOrganizationSite, LoginResponse, UserRoles } from 'api/models';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { AppStore } from '@app/stores/appStore';
import { AnnouncementsStore } from '@app/stores/announcementsStore';
import { interval } from 'rxjs';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-user-profile',
    templateUrl: './user-profile.component.html',
    styleUrls: ['./user-profile.component.scss'],
    imports: [
        MatButton,
        MatMenuTrigger,
        MatTooltip,
        MatIcon,
        NgClass,
        MatMenu,
        MatMenuItem,
        MatIconButton,
        TranslateModule,
    ],
})
export class UserProfileComponent implements OnInit {
    private appStore = inject(AppStore);
    private announcementsStore = inject(AnnouncementsStore);
    private router = inject(Router);
    sessionService = inject(SessionService);
    private translateService = inject(TranslateService);
    private storageService = inject(StorageService);
    private toastService = inject(ToastService);

    isExternal = input<boolean>();
    userSession = input<LoginResponse>();

    userMenuTrigger = viewChild<MatMenuTrigger>('userMenuTrigger');


    hideRating = signal(false);

    showRolesMenu = signal(false);
    favoriteOrganisationSite: string;

    UserRoles = UserRoles;
    forwardTo = signal(false);

    announcements = this.announcementsStore.announcements;
    allActiveAnnouncements = this.announcementsStore.allActiveAnnouncements;
    hasNewAnnouncements = this.announcementsStore.hasNewAnnouncements;

    // Store selectors
    currentOrganizationSite = this.appStore.currentOrganizationSite;
    currentSiteRoleSignal = this.appStore.currentSiteRole;
    availableOrganizationSites = this.appStore.availableOrganizationSites;

    /** Derived from appStore currentSiteRole signal — replaces subscribe-based currentSite. */
    get currentSite(): LoginOrganizationSite {
        return this.currentSiteRoleSignal()?.siteInfo;
    }

    async ngOnInit(): Promise<void> {
        this.router.events.pipe(untilDestroyed(this)).subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.forwardTo.set(
                    !event.url.includes('/external') ||
                        !event.url.includes('/tos')
                );
            }
        });

        const organizationSites =
            this.sessionService.getSession()?.organizationSites;

        const initialOrganizationSite = organizationSites.find(
            o => o._key.length !== 0
        );
        const site = initialOrganizationSite;

        // Update store with available sites
        this.appStore.setAvailableOrganizationSites(organizationSites);

        if (initialOrganizationSite !== undefined && site !== undefined) {
            // Update store with current site if not already set
            if (!this.currentOrganizationSite()) {
                this.appStore.setCurrentOrganizationSite(site);
            }
        }

        if (this.storageService.itemExists('favoriteOrganizationSiteKey')) {
            this.favoriteOrganisationSite = this.storageService.getItemAsObject(
                'favoriteOrganizationSiteKey'
            );
        }

        if (this.storageService.itemExists('currentSiteRole')) {
            const currentSiteRole =
                this.storageService.getItemAsObject('currentSiteRole');
            if (this.forwardTo()) {
                this.onChangeRole(
                    currentSiteRole.role,
                    currentSiteRole.siteInfo
                );
            }
        }

        this.showRolesMenu.set(
            organizationSites.some(
                site => site.consignor || site.consignee || site.carrier
            )
        );

        this.hideRating.set(
            this.storageService.getItem('hideRating') == 'true' ? true : false
        );

        // Load announcements immediately
        await this.announcementsStore.loadAndFilterAnnouncements();

        // Set up interval to reload announcements every 60 seconds
        interval(60000)
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.announcementsStore.loadAndFilterAnnouncements();
            });
    }

    getRoleTooltipContent(): string {
        let tooltipContent = '';
        if (this.sessionService.isSiteAdmin)
            tooltipContent = this.translateService
                .instant('ADMIN-PANEL.USERS.ROLES.SITEADMIN')
                .replaceAll(' ', '-');

        if (this.sessionService.isOrganizationAdmin)
            tooltipContent = this.translateService
                .instant('ADMIN-PANEL.USERS.ROLES.ORGADMIN')
                .replaceAll(' ', '-');

        if (this.sessionService.isServiceProvider) {
            tooltipContent = this.translateService
                .instant('ADMIN-PANEL.USERS.ROLES.SERVICEPROVIDER')
                .replaceAll(' ', '-');
        }

        return tooltipContent;
    }

    openSettings(): void {
        this.router.navigate(['/settings']);
    }

    onChangeRole(expectedRole: string, site: LoginOrganizationSite) {
        let role: string = expectedRole;
        switch (expectedRole) {
            case 'consignor':
                if (site.roles.includes(UserRoles.RemoteConsignor)) {
                    role = 'remote_consignor';
                }
                break;

            case 'consignee':
                if (site.roles.includes(UserRoles.RemoteConsignee)) {
                    role = 'remote_consignee';
                }
                break;

            case 'carrier':
                if (site.roles.includes(UserRoles.RemoteCarrier)) {
                    role = 'remote_carrier';
                }
                break;
        }

        const currentSiteRole = {
            role: role,
            siteInfo: site,
        };

        // Update store with new site and role
        this.appStore.updateCurrentOrganizationSiteAndRole(
            site,
            currentSiteRole
        );

        // Keep existing session service and storage updates for backward compatibility
        this.sessionService.currentSiteRole.set(currentSiteRole);
        this.sessionService.setCurrentOrganizationSite(site._key);
        this.storageService.setItem('currentSiteRole', currentSiteRole);
        const target: RoutingEndpoints = (<any>RoutingEndpoints)[expectedRole];
        this.router.navigate([target]);
    }

    setFavoriteSite(siteInfo: LoginOrganizationSite) {
        this.storageService.setItem(
            'favoriteOrganizationSiteKey',
            siteInfo._key
        );
    }
    onSetRatingDialog(opt: string) {
        switch (opt) {
            case 'false': {
                localStorage.setItem('hideRating', 'false');

                this.toastService.success(
                    this.translateService.instant(
                        'USER-PROFILE.FEEDBACK-CONFIRM.ON'
                    )
                );
                this.hideRating.set(false);
                break;
            }
            case 'true': {
                localStorage.setItem('hideRating', 'true');

                this.toastService.success(
                    this.translateService.instant(
                        'USER-PROFILE.FEEDBACK-CONFIRM.OFF'
                    )
                );
                this.hideRating.set(true);
                break;
            }
        }
    }

    onAnnouncementsMenuClosed(): void {
        this.announcementsStore.markAnnouncementsAsSeen();
    }
}
